import type { Directive } from 'vue'

const MARK_CLASS = 'gm-highlight'

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function getTextNodes(el: Node): Text[] {
  const walker = document.createTreeWalker(
    el,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        if ((node.parentElement as HTMLElement)?.classList?.contains(MARK_CLASS)) {
          return NodeFilter.FILTER_REJECT
        }
        return NodeFilter.FILTER_ACCEPT
      },
    }
  )
  const nodes: Text[] = []
  let current: Node | null
  while ((current = walker.nextNode())) {
    nodes.push(current as Text)
  }
  return nodes
}

function highlight(el: HTMLElement, query: string) {
  clearHighlights(el)

  const term = query.trim()
  if (!term) return

  const regex = new RegExp(`(${escapeRegex(term)})`, 'gi')

  for (const textNode of getTextNodes(el)) {
    const text = textNode.nodeValue ?? ''
    if (!regex.test(text)) { regex.lastIndex = 0; continue }
    regex.lastIndex = 0

    const fragment = document.createDocumentFragment()
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)))
      }
      const mark = document.createElement('mark')
      mark.className = `${MARK_CLASS} bg-state-success-bg text-inherit`
      mark.textContent = match[0]
      fragment.appendChild(mark)
      lastIndex = regex.lastIndex
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)))
    }

    textNode.parentNode?.replaceChild(fragment, textNode)
  }
}

function clearHighlights(el: HTMLElement) {
  el.querySelectorAll(`mark.${MARK_CLASS}`).forEach((mark) => {
    const parent = mark.parentNode
    if (!parent) return
    parent.replaceChild(document.createTextNode(mark.textContent ?? ''), mark)
    parent.normalize()
  })
}

export const vHighlight: Directive<HTMLElement, string | undefined> = {
  mounted(el, binding) {
    highlight(el, binding.value ?? '')
  },
  updated(el, binding) {
    if (binding.value !== binding.oldValue) {
      highlight(el, binding.value ?? '')
    }
  },
  beforeUnmount(el) {
    clearHighlights(el)
  },
}

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive('highlight', vHighlight)
})


